import React from 'react';
import { BookOpen, Calendar, Users, Trophy, Target, Flame } from 'lucide-react';

const ProfileStats = ({ stats }) => {
  const statCards = [
    {
      id: 'verses',
      label: 'Verses Memorized',
      value: stats?.versesMemorized ?? 0,
      icon: BookOpen,
      iconClass: 'text-blue-400',
      bgClass: 'bg-blue-500/10 border-blue-500/20',
    },
    {
      id: 'streak',
      label: 'Study Streak',
      value: `${stats?.studyStreak ?? 0} days`,
      icon: Flame,
      iconClass: 'text-orange-400',
      bgClass: 'bg-orange-500/10 border-orange-500/20',
    },
    {
      id: 'contributions',
      label: 'Verse Reflections',
      value: stats?.communityContributions ?? 0,
      icon: Users,
      iconClass: 'text-emerald-400',
      bgClass: 'bg-emerald-500/10 border-emerald-500/20',
    }
  ];

  const achievements = [
    {
      id: 'first_verse',
      name: 'First Steps',
      description: 'Memorized your first verse',
      icon: Target,
      earned: (stats?.versesMemorized ?? 0) >= 1,
      iconClass: 'text-blue-400'
    },
    {
      id: 'week_streak',
      name: 'Weekly Warrior',
      description: '7-day study streak',
      icon: Calendar,
      earned: (stats?.studyStreak ?? 0) >= 7,
      iconClass: 'text-orange-400'
    },
    {
      id: 'century_club',
      name: 'Century Club',
      description: '100+ verses memorized',
      icon: Trophy,
      earned: (stats?.versesMemorized ?? 0) >= 100,
      iconClass: 'text-primary'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Statistics Overview */}
      <div className="bg-card rounded-2xl border border-border shadow-warm-lg p-6">
        <div className="flex items-center space-x-3 mb-5">
          <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
            <Trophy className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-foreground">My Statistics</h2>
            <p className="text-muted-foreground text-xs">Track your spiritual growth journey</p>
          </div>
        </div>

        <div className="space-y-3">
          {statCards?.map((stat) => (
            <div
              key={stat?.id}
              className={`rounded-xl p-4 border ${stat?.bgClass}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="h-9 w-9 rounded-full bg-card/50 flex items-center justify-center">
                    <stat.icon className={`w-4 h-4 ${stat?.iconClass}`} />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">{stat?.label}</p>
                    <p className="text-xl font-bold text-foreground">{stat?.value}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Milestone Achievements */}
      <div className="bg-card rounded-2xl border border-border shadow-warm-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
            <Target className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-foreground">Milestones</h2>
            <p className="text-muted-foreground text-xs">Key achievements unlocked</p>
          </div>
        </div>
        <div className="space-y-2">
          {achievements?.map((a) => (
            <div
              key={a?.id}
              className={`flex items-center space-x-3 rounded-lg p-3 border ${
                a?.earned
                  ? 'bg-primary/5 border-primary/20' :'bg-muted/30 border-border opacity-50'
              }`}
            >
              <div className={`h-8 w-8 rounded-full flex items-center justify-center ${a?.earned ? 'bg-card' : 'bg-muted'}`}>
                <a.icon className={`w-4 h-4 ${a?.earned ? a?.iconClass : 'text-muted-foreground'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${a?.earned ? 'text-foreground' : 'text-muted-foreground'}`}>{a?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{a?.description}</p>
              </div>
              {a?.earned && (
                <span className="text-xs text-primary font-semibold">✓</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProfileStats;